import {
  Box,
  Card,
  CardBody,
  Flex,
  Text,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  VStack,
  HStack,
  Divider,
} from "@chakra-ui/react";
import { MoreVertical } from "lucide-react";

interface Procedure {
  id: number;
  name: string;
  description: string;
  price: string;
  status: "Active" | "Inactive";
  category?: string;
  duration?: string;
}

interface Permissions {
  can_view: boolean;
  can_edit?: boolean; 
  can_delete?: boolean; 
}

interface MobileProcedureCardProps {
  procedure: Procedure;
  permissions: Permissions;
  onViewDetails: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export default function MobileProcedureCard({
  procedure,
  permissions,
  onViewDetails,
  onEdit,
  onDelete,
}: MobileProcedureCardProps) {
  return (
    <Card mb={3} variant="outline" borderRadius="lg" overflow="hidden">
      <CardBody p={4}>
        <Flex justify="space-between" align="flex-start" mb={3}>
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="md" fontWeight="semibold" color="gray.800">
              {procedure.name}
            </Text>
            {procedure.category && (
              <Badge
                colorScheme="purple"
                fontSize="xs"
                px={2}
                py={0.5}
                borderRadius="md"
                variant="subtle"
              >
                {procedure.category}
              </Badge>
            )}
          </VStack>
          {(permissions.can_view || permissions.can_edit || permissions.can_delete) && (
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<MoreVertical size={18} />}
                variant="ghost"
                size="sm"
                aria-label="Options"
                ml={2}
              />
              <MenuList fontSize="sm">
                {permissions.can_view && (
                  <MenuItem onClick={onViewDetails}>View Details</MenuItem>
                )}
                {permissions.can_edit && onEdit && (
                  <MenuItem onClick={onEdit}>Edit Procedure</MenuItem>
                )}
                {permissions.can_delete && onDelete && (
                  <MenuItem color="red.600" onClick={onDelete}>
                    Delete Procedure
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          )}
        </Flex>

        <VStack align="stretch" spacing={2}>
          <Box>
            <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
              Description
            </Text>
            <Text fontSize="sm" color="gray.700" lineHeight="1.5">
              {procedure.description}
            </Text>
          </Box>

          <Divider />

          <HStack justify="space-between" flexWrap="wrap" gap={2}>
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
                Price
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                {procedure.price}
              </Text>
            </Box>

            {procedure.duration && (
              <Box>
                <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
                  Duration
                </Text>
                <Text fontSize="sm" color="gray.700">
                  {procedure.duration}
                </Text>
              </Box>
            )}
          </HStack>

          <Divider />

          <Flex justify="space-between" align="center">
            <Text fontSize="xs" color="gray.500" fontWeight="medium">
              Status
            </Text>
            <Badge
              colorScheme={procedure.status === "Active" ? "green" : "red"}
              fontSize="xs"
              px={3}
              py={1}
              borderRadius="md"
              variant="outline"
              textTransform="none"
            >
              {procedure.status}
            </Badge>
          </Flex>
        </VStack>
      </CardBody>
    </Card>
  );
}
